import React, { useState } from "react"
import { connect } from "react-redux"
import cn from "classnames"
import { useTranslation, withTranslation, type WithTranslation } from "react-i18next"
import type { State } from "@/signomatic/store/reducers"
import Toolbox from "@/signomatic/components/toolbox"
import ShapeIcons from "@/images/signomatic/shapes"
import AutoGrid from "@/signomatic/components/auto_grid"
import type { ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { optionForcedState } from "@/signomatic/app/sign_validator"
import { matchArray } from "@/signomatic/util"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"

const reason = (type: string | null) => {
  switch (type) {
    case "size":
      return "toolbox.shape_disabled_size"
    case "fixmethod":
      return "toolbox.shape_disabled_fix_method"
    default:
      return ""
  }
}

interface ShapeProps {
  name?: string | null
  enabled?: boolean | null
  active: boolean
  update: () => void
}

const Shape = ({ name, enabled, active, update }: ShapeProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("item", "selection", `shape-${name}`, { disabled: !enabled, active })} onClick={update}>
      <div className="image">{name && ShapeIcons[name] ? React.createElement(ShapeIcons[name]) : ""}</div>
      <div className="name paragraph-2xs center mb-0">{t(`shape_type.${name}`)}</div>
    </div>
  )
}

interface ShapeToolboxStateProps {
  data: SignData
  signState: SignState
}

type ShapeToolboxProps = ShapeToolboxStateProps & ToolboxStripItemProps & WithTranslation

const ShapeToolbox: React.FC<ShapeToolboxProps> = ({ sign, signState, data, t }) => {
  const [disabledMessage, setDisabledMessage] = useState<string | null>()

  const renderShapes = () => {
    let contour = data.shapes.find(s => s.name === "contour")

    if (contour && contour.filters && contour.filters.admin && !ClientData.admin) {
      contour = undefined
    }

    return (
      <>
        <div className="inline-toolbox">
          <AutoGrid>
            {data.shapes.map(shape => {
              if (shape.name === "contour") {
                return null
              }

              let enabled = shape.enabled
              let forcedState = {}

              if (!enabled && matchArray(shape.disabled_reason, ["size"])) {
                enabled = true

                forcedState = optionForcedState(shape, signState, ["size"])
              }

              return (
                <Shape
                  key={shape.name}
                  name={shape.name}
                  active={signState.shape === shape.name}
                  enabled={enabled}
                  update={() => {
                    if (enabled) {
                      sign.updateState({ shape: shape.name, ...forcedState })
                      setDisabledMessage(null)
                    } else if (shape.disabled_reason) {
                      setDisabledMessage(shape.disabled_reason[0])
                    }
                  }}
                />
              )
            })}
          </AutoGrid>
          {disabledMessage && (
            <div className="disabled-reason paragraph-2xs bold mb-0">{t(reason(disabledMessage))}</div>
          )}
        </div>
        {contour && (
          <div className="inline-toolbox sub">
            <h4>{t("shape_type.contour")}</h4>
            <AutoGrid>
              <Shape
                name="contour_small"
                active={signState.shape === contour.name && signState.contour === "s"}
                enabled
                update={() => {
                  sign.updateState({ shape: contour.name, contour: "s" })
                  setDisabledMessage(null)
                }}
              />
              <Shape
                name="contour_medium"
                active={signState.shape === contour.name && signState.contour === "m"}
                enabled
                update={() => {
                  sign.updateState({ shape: contour.name, contour: "m" })
                  setDisabledMessage(null)
                }}
              />
              <Shape
                name="contour_large"
                active={signState.shape === contour.name && signState.contour === "l"}
                enabled
                update={() => {
                  sign.updateState({ shape: contour.name, contour: "l" })
                  setDisabledMessage(null)
                }}
              />
            </AutoGrid>
          </div>
        )}
      </>
    )
  }

  return window.innerWidth <= 1023 ? (
    <Toolbox className="toolbox-shape">
      <Scrollable scrollToActive scrollActiveToolboxDelay>
        {renderShapes()}
      </Scrollable>
    </Toolbox>
  ) : (
    <Toolbox className="toolbox-shape">{renderShapes()}</Toolbox>
  )
}

export default connect(({ signState, currentSignData }: State) => ({
  signState,
  data: currentSignData,
}))(withTranslation()(ShapeToolbox))
